import type { CaptureOptions, PostHog } from "posthog-js";

/**
 * PostHog transport. This module knows how to load the SDK and hand it an
 * event; it does not decide *what* is worth capturing — see `./index.ts` for
 * the event registry and the fan-out across sinks.
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

/**
 * Same-origin path served by the Worker's PostHog reverse proxy (`src/server.ts`).
 * Content blockers blocklist `*.i.posthog.com`, which silently drops ingestion
 * *and* the `recorder.js` session-replay asset; routing both through our own
 * origin keeps them off those lists.
 *
 * `VITE_POSTHOG_HOST` overrides this with a direct upstream host, for
 * environments where the Worker proxy isn't in front of the app.
 */
const POSTHOG_PROXY_PATH = "/ph";
const POSTHOG_HOST_OVERRIDE = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

/**
 * PostHog *app* URL. posthog-js derives this from `api_host` by string
 * substitution, which yields nonsense for a custom proxy host — set it
 * explicitly or toolbar/replay deep links point at `/ph`.
 */
const POSTHOG_UI_HOST =
  (import.meta.env.VITE_POSTHOG_UI_HOST as string | undefined) || "https://eu.posthog.com";

/** Cap on events buffered pre-init, so a stuck import can't grow this forever. */
const MAX_BUFFERED_EVENTS = 50;

let posthogInstance: PostHog | null = null;
let loadPromise: Promise<void> | null = null;

interface BufferedEvent {
  event: string;
  props?: Record<string, unknown>;
  options?: CaptureOptions;
  /** Wall-clock time the event actually happened, not the time it was flushed. */
  timestamp: Date;
}

let bufferedEvents: BufferedEvent[] = [];
let droppedEvents = 0;

/** Replays everything captured before `posthog.init()` resolved, in order. */
function flushBufferedEvents(instance: PostHog): void {
  for (const { event, props, options, timestamp } of bufferedEvents) {
    instance.capture(event, props, { ...options, timestamp });
  }
  bufferedEvents = [];
}

async function initPostHog(): Promise<void> {
  const { default: posthog } = await import("posthog-js");

  posthog.init(POSTHOG_KEY as string, {
    api_host: POSTHOG_HOST_OVERRIDE || `${window.location.origin}${POSTHOG_PROXY_PATH}`,
    ui_host: POSTHOG_UI_HOST,
    autocapture: true,
    capture_pageview: false,
    person_profiles: "identified_only",
    // Trim the lazy-loaded extensions we don't use off the critical path:
    // surveys.js (~32 KiB), dead-clicks-autocapture.js (~6 KiB), and
    // web-vitals.js (~3 KiB). Session replay + autocapture stay on.
    disable_surveys: true,
    capture_dead_clicks: false,
    capture_performance: { web_vitals: false },
    session_recording: {
      maskAllInputs: true,
    },
  });

  // This project is shared with the dashboard (app.axy.digital), which registers
  // `app: "dashboard"`. Both surfaces share one event stream and one
  // `.axy.digital` cookie, so every insight needs a way to tell them apart.
  posthog.register({ app: "landing" });

  posthogInstance = posthog;
  flushBufferedEvents(posthog);
}

/**
 * Lazily loads and initialises PostHog. No-ops if there is no project key or
 * we're not running in a browser; concurrent/repeat calls share one init.
 * Meant to be called after the window `load` event so the SDK never sits on
 * the critical render path (mirrors the GTM loader in `src/routes/__root.tsx`).
 */
export function loadPostHog(): Promise<void> {
  if (!POSTHOG_KEY || typeof window === "undefined") {
    return Promise.resolve();
  }
  loadPromise ??= initPostHog();
  return loadPromise;
}

/**
 * Sends an event to PostHog, buffering it when the SDK hasn't finished
 * initialising.
 *
 * The player and the SDK loader both start on the window `load` event, so the
 * first video events (`vsl_impression`, `vsl_autoplay`) routinely fire while
 * `import("posthog-js")` is still in flight. Dropping them would silently
 * corrupt every rate in SPEC.md §3.3, since those two events are the
 * *denominators* — and the bias would track device/connection speed. Buffered
 * events keep their original timestamp so ordering survives the flush.
 */
export function posthogCapture(
  event: string,
  props?: Record<string, unknown>,
  options?: CaptureOptions,
): void {
  if (!POSTHOG_KEY || typeof window === "undefined") return;

  if (posthogInstance) {
    posthogInstance.capture(event, props, options);
    return;
  }

  if (bufferedEvents.length >= MAX_BUFFERED_EVENTS) {
    droppedEvents += 1;
    if (import.meta.env.DEV) {
      console.warn(
        `[analytics] buffer full (${MAX_BUFFERED_EVENTS}), dropped "${event}" ` +
          `(${droppedEvents} dropped total) — did loadPostHog() ever resolve?`,
      );
    }
    return;
  }

  bufferedEvents.push({ event, props, options, timestamp: new Date() });
}
